import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Cookie, Settings, X, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'cookie_consent';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always required
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a small delay for better UX
      setTimeout(() => setShowBanner(true), 500);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(consent);
        setCookiePreferences(savedPreferences);
      } catch (e) {
        // Invalid consent, show banner
        setShowBanner(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(allAccepted));
    setCookiePreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptNecessary = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(onlyNecessary));
    setCookiePreferences(onlyNecessary);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(cookiePreferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setCookiePreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={() => {}} />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
        <div className="max-w-4xl mx-auto bg-card rounded-xl shadow-2xl border-2 border-[#FDB913] overflow-hidden">
          {/* Header */}
          <div className="bg-[#FDB913]/10 border-b border-[#FDB913]/30 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-[#FDB913] rounded-full p-2">
                <Cookie size={24} className="text-[#1a2332]" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Cookie Instellingen</h2>
            </div>
            {showSettings && (
              <button 
                onClick={() => setShowSettings(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={24} />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {!showSettings ? (
              <>
                <p className="text-muted-foreground mb-4">
                  Deze website maakt gebruik van cookies om je ervaring te verbeteren. 
                  Cookies helpen ons om de website goed te laten functioneren, het gebruik te analyseren 
                  en relevante content te tonen.
                </p>
                <p className="text-muted-foreground text-sm mb-6">
                  Lees meer in ons{' '}
                  <Link to="/privacybeleid" className="text-[#FDB913] hover:underline font-medium">
                    Privacybeleid
                  </Link>
                  .
                </p>
                
                {/* Quick Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={handleAcceptAll}
                    className="flex-1 bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold"
                  >
                    Alle Cookies Accepteren
                  </Button>
                  <Button 
                    onClick={handleAcceptNecessary}
                    variant="outline"
                    className="flex-1 border-border"
                  >
                    Alleen Noodzakelijk
                  </Button>
                  <Button 
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    className="flex-1 border-[#FDB913] text-foreground"
                  >
                    <Settings size={16} className="mr-2" />
                    Instellingen
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Detailed Settings */}
                <p className="text-muted-foreground mb-6">
                  Hieronder kun je per categorie aangeven welke cookies je wilt accepteren. 
                  Noodzakelijke cookies zijn altijd actief omdat de website anders niet goed werkt.
                </p>

                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <div className="flex items-center justify-between p-4 bg-secondary rounded-lg border border-border">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Label className="font-semibold text-foreground">Noodzakelijke Cookies</Label>
                        <span className="text-xs bg-green-900/50 text-green-400 px-2 py-0.5 rounded">Vereist</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Deze cookies zijn essentieel voor het functioneren van de website, zoals inloggen en voorkeuren onthouden.
                      </p>
                    </div>
                    <Switch 
                      checked={cookiePreferences.necessary} 
                      disabled={true}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>

                  {/* Functional Cookies */}
                  <div className="flex items-center justify-between p-4 bg-secondary rounded-lg border border-border">
                    <div className="flex-1">
                      <Label className="font-semibold text-foreground">Functionele Cookies</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Deze cookies zorgen voor extra functionaliteiten zoals gepersonaliseerde content en onthouden van je voorkeuren.
                      </p>
                    </div>
                    <Switch 
                      checked={cookiePreferences.functional} 
                      onCheckedChange={() => togglePreference('functional')}
                      className="data-[state=checked]:bg-[#FDB913]"
                    />
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-4 bg-secondary rounded-lg border border-border">
                    <div className="flex-1">
                      <Label className="font-semibold text-foreground">Analytische Cookies</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Deze cookies helpen ons te begrijpen hoe bezoekers de website gebruiken, zodat we deze kunnen verbeteren.
                      </p>
                    </div>
                    <Switch 
                      checked={cookiePreferences.analytics} 
                      onCheckedChange={() => togglePreference('analytics')}
                      className="data-[state=checked]:bg-[#FDB913]"
                    />
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between p-4 bg-secondary rounded-lg border border-border">
                    <div className="flex-1">
                      <Label className="font-semibold text-foreground">Marketing Cookies</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Deze cookies worden gebruikt om advertenties relevanter te maken en je surfgedrag bij te houden.
                      </p>
                    </div>
                    <Switch 
                      checked={cookiePreferences.marketing} 
                      onCheckedChange={() => togglePreference('marketing')}
                      className="data-[state=checked]:bg-[#FDB913]"
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={handleSavePreferences}
                    className="flex-1 bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold"
                  >
                    <Shield size={16} className="mr-2" />
                    Voorkeuren Opslaan
                  </Button>
                  <Button 
                    onClick={handleAcceptAll}
                    variant="outline"
                    className="flex-1"
                  >
                    Alle Accepteren
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-secondary px-6 py-3 text-center border-t border-border">
            <p className="text-xs text-muted-foreground">
              Door deze website te gebruiken, ga je akkoord met ons{' '}
              <Link to="/privacybeleid" className="text-[#FDB913] hover:underline">privacybeleid</Link>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
